import {Trans} from '@lingui/macro';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {CellProps, Column, useFlexLayout, useGlobalFilter, useTable} from 'react-table';
import {FixedSizeList} from 'react-window';
import styled from 'styled-components';

import {computeScrollbarWidth} from '../../../libs/shared/helpers/src/browser/domHelpers';
import {Handler, RendererOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {PopoverCoordinator} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {useConstant} from '../../../srcCommon/helpers/hookHelpers';
import {useMeasureElement} from '../../helpers/hooks/domHookHelpers';
import {
  brandDataFieldsTableColumnId,
  DataFieldsTableColumnId,
  DataFieldsTableColumnModel,
  DataFieldsTableColumnTypesEnum,
  DataFieldsTableData,
} from '../../models/api/dataFieldsTableModel';
import {DataFieldsTableCell} from './dataFieldsTableCell';
import {DataFieldsTableColumnDialog} from './dataFieldsTableColumnDialog';
import {DataFieldsTableHeader} from './dataFieldsTableHeader';
import {DataFieldsTableOrderCell} from './dataFieldsTableOrderCell';
import {DataFieldsTableRow} from './dataFieldsTableRow';
import {StyledDataFieldsTableHeader, StyledRemoveRowButton} from './styles';

/*
 * Constants.
 */

const DATA_TABLE_CELL_MIN_WIDTH = 250;
const DATA_TABLE_CELL_HEIGHT = 40;
const ORDER_CELL_WIDTH = 88;
const ORDER_COLUMN_ID = brandDataFieldsTableColumnId('__order');
const ADD_COLUMN_ID = brandDataFieldsTableColumnId('__add');

/*
 * Styles.
 */

export const StyledTableDiv = styled.div`
  border-spacing: 0;
`;

export const StyledWrapper = styled.div`
  height: 100%;
  width: min-content;
  min-width: 100%;
`;

export const StyledTableFooter = styled.div`
  height: ${DATA_TABLE_CELL_HEIGHT};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  ${flexCenter};
  padding: 7px;
`;

/*
 * Props.
 */

export interface DataFieldsTableProps {
  columns: Array<DataFieldsTableColumnModel>;
  rows: Array<DataFieldsTableData>;

  onUpdateCell: (row: number, column: DataFieldsTableColumnModel, value: string | undefined) => void;
  onAddColumn: (data: Omit<DataFieldsTableColumnModel, 'id'>) => void;
  onEditColumn: (id: DataFieldsTableColumnId, name: Partial<DataFieldsTableColumnModel>) => void;
  onDeleteColumn: (id: DataFieldsTableColumnId) => void;
  columnValidator: RendererOf<Partial<DataFieldsTableColumnModel>>;

  onAddRow: Handler;
  onDeleteRow: (index: number) => void;
  onReorderRow?: (currentIndex: number, newIndex: number) => void;

  emptyState?: ReactNode;

  searchText?: string;
}

/*
 * Component.
 */

export const DataFieldsTable: FC<DataFieldsTableProps> = ({
  columns,
  rows: data,
  onUpdateCell,
  onEditColumn,
  onAddColumn,
  onDeleteColumn,
  onReorderRow,
  onAddRow,
  onDeleteRow,
  columnValidator,
  emptyState,
  searchText,
}) => {
  const scrollbarWidth = useConstant(() => computeScrollbarWidth());
  const tableColumns: Array<Column<DataFieldsTableData>> = useMemo(() => {
    const orderColumn: Column<DataFieldsTableData> = {
      id: ORDER_COLUMN_ID,
      Header: <Trans id="data-fields-table-order-column-name">Order</Trans>,
      // eslint-disable-next-line react/no-unstable-nested-components
      accessor: (d, index) => (
        <DataFieldsTableOrderCell
          order={index + 1}
          onChange={(newOrder) => onReorderRow?.(index, newOrder - 1)}
        />
      ),
      width: ORDER_CELL_WIDTH,
      maxWidth: ORDER_CELL_WIDTH,
    };

    const dataColumns = columns.map(
      (column): Column<DataFieldsTableData> => ({
        id: column.id,
        Header: (
          <DataFieldsTableHeader
            column={column}
            onEditColumn={(newColumn) => onEditColumn(column.id, newColumn)}
            columnValidator={columnValidator}
            onDeleteColumn={() => onDeleteColumn(column.id)}
          />
        ),
        accessor: column.id,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({value, row}: CellProps<DataFieldsTableData, string | undefined>) => (
          <DataFieldsTableCell
            column={column}
            data={value}
            onChange={(newValue) => onUpdateCell(row.index, column, newValue)}
          />
        ),
        minWidth: DATA_TABLE_CELL_MIN_WIDTH,
      }),
    );

    const addColumnColumn: Column<DataFieldsTableData> = {
      id: ADD_COLUMN_ID,
      Header: (
        <PopoverCoordinator
          renderButton={(isOpen) => (
            <IconButton
              iconName="plusCircle"
              label={<Trans id="data-fields-table-add-column-icon-label">Add column</Trans>}
              isOpen={isOpen}
              hasHover={isOpen}
            />
          )}
          renderModal={(popoverProps) => (
            <DataFieldsTableColumnDialog
              {...popoverProps}
              mode="create"
              columnValidator={columnValidator}
              onSubmit={(newColumn) =>
                onAddColumn({name: '', type: DataFieldsTableColumnTypesEnum.STRING, ...newColumn})
              }
            />
          )}
        />
      ),
      // eslint-disable-next-line react/no-unstable-nested-components
      accessor: (_, index) => (
        <StyledRemoveRowButton
          tabIndex={-1}
          onClick={() => onDeleteRow(index)}
          label={<Trans id="data-fields-table-remove-row-button-label">Remove row</Trans>}
          iconName="trashSmall"
        />
      ),
      width: 45,
      maxWidth: 45,
    };

    if (onReorderRow) {
      return [orderColumn, ...dataColumns, addColumnColumn];
    }
    return [...dataColumns, addColumnColumn];
  }, [
    columns,
    onReorderRow,
    columnValidator,
    onEditColumn,
    onDeleteColumn,
    onUpdateCell,
    onAddColumn,
    onDeleteRow,
  ]);

  const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setGlobalFilter} = useTable(
    {columns: tableColumns, data},
    useFlexLayout,
    useGlobalFilter,
  );

  useEffect(() => setGlobalFilter(searchText), [data, setGlobalFilter, searchText]);

  const [ref, bounds] = useMeasureElement();
  const listRef = useRef<HTMLDivElement>(null);

  const [listWidth, setListWidth] = useState('100%');
  useLayoutEffect(() => {
    const listElement = listRef.current;
    const wrapperElement = listElement?.parentElement;
    if (!listElement || !wrapperElement) {
      return;
    }

    const hasScroll = listElement.clientHeight > wrapperElement.clientHeight;
    setListWidth(hasScroll ? `calc(100% + ${scrollbarWidth}px)` : '100%');
  }, [rows.length, searchText, scrollbarWidth]);

  const renderRow = useCallback(
    ({index, style, data: rowsData}) => {
      if (index === rowsData.length) {
        return (
          <StyledTableFooter style={style}>
            <IconButton
              onClick={onAddRow}
              iconName="plusCircle"
              label={<Trans id="data-fields-table-add-row-button-label">Add row</Trans>}
            />
          </StyledTableFooter>
        );
      }

      const row = rowsData[index];
      prepareRow(row);

      return <DataFieldsTableRow row={row} style={style} />;
    },
    [prepareRow, onAddRow],
  );

  return (
    <StyledWrapper ref={ref}>
      <StyledTableDiv {...getTableProps()}>
        <div>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <StyledDataFieldsTableHeader {...column.getHeaderProps()}>
                  {column.render('Header')}
                </StyledDataFieldsTableHeader>
              ))}
            </div>
          ))}
        </div>

        {rows.length === 0 && emptyState ? (
          emptyState
        ) : (
          <div {...getTableBodyProps()}>
            <FixedSizeList
              innerRef={listRef}
              height={bounds.height - DATA_TABLE_CELL_HEIGHT}
              // We add an element for the footer
              itemCount={rows.length + 1}
              itemSize={DATA_TABLE_CELL_HEIGHT}
              // Leave enough space for the scrollbar to render in browsers where it's always rendered.
              width={listWidth}
              itemData={rows}
            >
              {renderRow}
            </FixedSizeList>
          </div>
        )}
      </StyledTableDiv>
    </StyledWrapper>
  );
};
